import { useEffect, useState, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TypeHeroToastProvider, ToastStack } from '@/shared/components/toast-system';
import LoggerBoundary from '../microscope/LoggerBoundary';
import { startReplayCapture } from '../lib/replay';
import microscopeConfig from '../config/microscope';
import {
  fetchLogToken,
  getLogToken,
  clearLogToken,
  isTokenNearExpiry,
  isLoggingEnabled,
} from '../lib/logToken';

const App = () => {
  const { user, status } = useSelector((state) => state.auth);
  const [logToken, setLogToken] = useState(null);
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const loggingDisabledForSession = useRef(false);

  useEffect(() => {
    startReplayCapture();
  }, []);

  useEffect(() => {
    if (!user) {
      clearLogToken();
      setLogToken(null);
      loggingDisabledForSession.current = false;
      return;
    }

    if (loggingDisabledForSession.current) return;

    fetchLogToken().then(() => {
      const enabled = isLoggingEnabled();
      setLogToken(getLogToken());
      setLoggingEnabled(enabled);
      if (!enabled) {
        loggingDisabledForSession.current = true;
      }
    });
  }, [user]);

  useEffect(() => {
    if (!logToken || !loggingEnabled || loggingDisabledForSession.current) return;

    const id = setInterval(async () => {
      if (isTokenNearExpiry()) {
        await fetchLogToken();
        const enabled = isLoggingEnabled();
        setLogToken(getLogToken());
        setLoggingEnabled(enabled);
        if (!enabled) {
          loggingDisabledForSession.current = true;
        }
      }
    }, 60000);

    return () => clearInterval(id);
  }, [logToken, loggingEnabled]);

  useEffect(() => {
    if (!window.pendo) return;
    if (status === 'idle' || status === 'loading') return;

    if (user && user.id) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      const subStatus = user.subscription?.status || 'none';
      const subPlan = user.subscription?.planName || user.subscription?.planId || 'none';

      const pendoPayload = {
        visitor: {
          id: user.id,
          email: user.email,
          full_name: fullName,
          is_two_factor_enabled: user.isTwoFactorEnabled,
          has_profile_picture: !!user.profilePicture,
          permissions: user.permissions,
          author_bio: user.authorBio || '',
        },
        account: {
          id: user.firmId,
          name: `Firm ${user.firmId}`,
          payment_customer_id: user.paymentCustomerId,
          is_payment_enabled: user.isPaddleEnabled,
          subscription_status: subStatus,
          subscription_plan: subPlan,
          word_count_limit: user.continueWritingWordCount,
        },
      };

      if (window.pendo.isReady && window.pendo.isReady()) {
        window.pendo.identify(pendoPayload);
      } else {
        window.pendo.initialize(pendoPayload);
      }
    } else {
      if (!window.pendo.isReady || !window.pendo.isReady()) {
        window.pendo.initialize();
      }
    }
  }, [user, status]);

  const microscopeUser = user
    ? {
        id: user.id,
        email: user.email,
        name: [user.firstName, user.lastName].filter(Boolean).join(' '),
      }
    : null;

  return (
    <TypeHeroToastProvider>
      <LoggerBoundary
        enabled={loggingEnabled}
        logToken={logToken}
        appName={microscopeConfig.appName}
        environment={microscopeConfig.environment}
        user={microscopeUser}
      >
        <Outlet />
        <ToastStack />
      </LoggerBoundary>
    </TypeHeroToastProvider>
  );
};

export default App;